/**
 * dropOffReasonsStaff.tsx — WHY AN APPROVED PATIENT HAS NOT PAID, read back on
 * the staff side (T-2, 2026-09-11).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * The patient half — "what's holding you back?" on /:clinicSlug/not-ready
 * (#411) — records one reason on the deal (`dealCard.dropOffReason`). This
 * module is what the staff surfaces need to read it back: the staff wording,
 * which reasons are a clinical question, when a board card should say it, and
 * the chip. The board card, the drawer row, the filter panel and the Insights
 * ranking all draw from here, so one answer cannot be worded or classified four
 * different ways.
 *
 * ⚠️ TWO REASONS ARE A CLINICAL QUESTION, AND THE FLAG IS ABOUT THE REPLY. A
 * patient "worried about side effects" or "not sure the treatment is right" is
 * asking something only a clinician should answer; a discount or a sales call in
 * reply to a safety worry is the wrong answer to a safety concern. So those two
 * never render as a plain reason on the board: the card says "Clinician to
 * call", in the danger pair, with the patient's reason in the accessible name
 * and on hover. The words carry it, the colour only repeats it (WCAG 1.4.1).
 *
 * ⚠️ AN ANSWER IS A LIVE OBJECTION ONLY WHILE THE PATIENT IS STILL UNPAID. The
 * column keeps its value after the patient pays — it is history, and the
 * ranking wants it — so a Won card would otherwise read "The cost" for ever.
 * The card shows the reason only in the unpaid cohort, the lanes the server's
 * write gate accepts an answer in. The one exception is while the reason filter
 * is applied: the card is on screen BECAUSE of its reason then, and a Won card
 * with no mark would leave staff asking why it matched. There it says it
 * quietly, as something said before they moved on. The drawer always shows it.
 *
 * THE VOCABULARY IS A MIRROR. clinic-service owns DROP_OFF_REASONS,
 * CLINICAL_DROP_OFF_REASONS and the staff labels (src/deals/drop-off-reasons.ts).
 * The keys here are typed off the patient page's own list, so the compiler
 * refuses a label for a reason the patient cannot pick, or a reason with no
 * label. The server validates the column on the way out, so an unknown value
 * arrives as null and renders nothing. TODO(CTO): retire this and the patient
 * copy when contracts carries the vocabulary.
 */
import type { CSSProperties } from 'react';
import type { DealCard, DealStage } from '../api/types';
import { DROP_OFF_REASON_OPTIONS, type DropOffReasonKey } from '../journey/dropOffReasons';
import { formatUtcDate } from '../lib/format';
import { Alert } from '../ui/kit';
import { chipStyle } from './statusChips';

/** clinic-service `DROP_OFF_REASON_LABELS`, verbatim — the staff wording. */
export const DROP_OFF_REASON_STAFF_LABEL: Readonly<Record<DropOffReasonKey, string>> = {
  cost: 'The cost',
  comparing_providers: 'Comparing providers',
  unsure_about_treatment: 'Not sure the treatment is right',
  worried_about_side_effects: 'Worried about side effects',
  want_to_speak_to_someone: 'Wants to speak to someone',
  not_ready: 'Not ready yet',
  decided_against: 'Decided not to go ahead',
  other: 'Something else',
};

/** Every reason, in the patient page's order — which is clinic-service's order. */
export const DROP_OFF_REASON_KEYS: readonly DropOffReasonKey[] = DROP_OFF_REASON_OPTIONS.map((o) => o.key);

/**
 * clinic-service `CLINICAL_DROP_OFF_REASONS`. Deliberately NOT the patient
 * page's `PEOPLE_ROUTED_REASONS`, which adds "want to speak to someone": that
 * reason is routed to a PERSON, and any person will do. These two are routed to
 * a CLINICIAN.
 */
export const CLINICAL_DROP_OFF_REASONS: readonly DropOffReasonKey[] = [
  'unsure_about_treatment',
  'worried_about_side_effects',
];

export function isDropOffReasonKey(value: unknown): value is DropOffReasonKey {
  return typeof value === 'string' && (DROP_OFF_REASON_KEYS as readonly string[]).includes(value);
}

export function isClinicalDropOffReason(reason: DropOffReasonKey): boolean {
  return CLINICAL_DROP_OFF_REASONS.includes(reason);
}

/**
 * The unpaid cohort's lanes — clinic-service `PLAN_AWAITING_PAYMENT_STAGES`. The
 * write gate there accepts an answer only in these lanes AND with no authorised
 * card, so this pair of facts is exactly "the answer is still open".
 */
export const DROP_OFF_OPEN_STAGES: readonly DealStage[] = [
  'plan_sent',
  'awaiting_clinician_followup',
  'awaiting_admin_followup',
];

export interface DropOffChip {
  reason: DropOffReasonKey;
  clinical: boolean;
  /** Still unpaid, so the reason is a live objection rather than history. */
  open: boolean;
}

/**
 * Should the board card say why they have not paid, and as what? Null when it
 * should say nothing — no reason recorded, a value this build does not know, or
 * a reason that is only history on a board that is not filtering by reason.
 */
export function dropOffChipFor(
  card: Pick<DealCard, 'dropOffReason' | 'stage' | 'holdAuthorisedAt'>,
  reasonFilterApplied: boolean,
): DropOffChip | null {
  const reason = card.dropOffReason;
  if (!isDropOffReasonKey(reason)) return null;
  const open = DROP_OFF_OPEN_STAGES.includes(card.stage) && !card.holdAuthorisedAt;
  if (!open && !reasonFilterApplied) return null;
  return { reason, clinical: isClinicalDropOffReason(reason), open };
}

/** What the chip shows, and the fuller sentence its accessible name and hover carry. */
export function dropOffChipText(chip: DropOffChip): { shown: string; name: string } {
  const label = DROP_OFF_REASON_STAFF_LABEL[chip.reason];
  if (!chip.open) {
    return { shown: label, name: `Said before they moved on: ${label}${chip.clinical ? ' (a clinical question)' : ''}` };
  }
  if (chip.clinical) {
    return {
      shown: 'Clinician to call',
      name: `Clinician to call. The patient said: ${label}. A clinical question, not a sales conversation.`,
    };
  }
  return { shown: label, name: `Why they have not paid: ${label}` };
}

/** The filter panel's tick label — the clinical pair says who acts on it. */
export function dropOffFilterLabel(reason: DropOffReasonKey): string {
  const label = DROP_OFF_REASON_STAFF_LABEL[reason];
  return isClinicalDropOffReason(reason) ? `${label} — clinician to call` : label;
}

/** The toolbar chip for an applied reason. */
export function dropOffFilterChipLabel(reason: DropOffReasonKey): string {
  return `Reason: ${DROP_OFF_REASON_STAFF_LABEL[reason]}`;
}

/**
 * The drawer row: the reason, and when and where the patient FIRST gave it.
 * Shown whatever happened next — the drawer is where history is read. Null when
 * no reason is recorded, which is the common case (most people never say).
 */
export function dropOffDrawerText(
  card: Pick<DealCard, 'dropOffReason' | 'dropOffReasonAt' | 'dropOffReasonSource'>,
): string | null {
  if (!isDropOffReasonKey(card.dropOffReason)) return null;
  const label = DROP_OFF_REASON_STAFF_LABEL[card.dropOffReason];
  const tail = [
    card.dropOffReasonAt ? `first given ${formatUtcDate(card.dropOffReasonAt)}` : null,
    card.dropOffReasonSource === 'patient_plan_page' ? 'on their plan page' : null,
  ]
    .filter(Boolean)
    .join(' ');
  return tail ? `${label} — ${tail}` : label;
}

const OPEN_REASON_TONE = { bg: 'var(--gps-surface-2)', text: 'var(--gps-text)' };
const OPEN_CLINICAL_TONE = { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' };
const HISTORY_TONE = { bg: 'transparent', text: 'var(--gps-muted)' };

/** One line, however long the label: a wrapped chip makes one card taller than its lane-mates. */
const clipStyle: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };

/**
 * The board card's reason chip. `role="img"` with the full sentence as its name,
 * the card row's existing idiom for a chip whose visible text is a summary (the
 * eligibility chip that carries its closure reason does the same).
 */
export function DropOffReasonChip({ chip }: { chip: DropOffChip }) {
  const { shown, name } = dropOffChipText(chip);
  const tone = !chip.open ? HISTORY_TONE : chip.clinical ? OPEN_CLINICAL_TONE : OPEN_REASON_TONE;
  const style: CSSProperties = {
    ...chipStyle(tone),
    maxWidth: '100%',
    // A ghost chip pays its border out of its padding, as the questionnaire
    // ghosts do, so it stands exactly as tall as a filled one (CHIP_HEIGHT).
    ...(chip.open ? {} : { border: '1px dashed var(--gps-border-strong)', padding: '3px 9px' }),
  };
  return (
    <span role="img" aria-label={name} title={name} data-drop-off-reason={chip.reason} style={style}>
      {chip.open && chip.clinical ? <PhoneGlyph /> : <SpeechGlyph />}
      <span style={clipStyle}>{shown}</span>
    </span>
  );
}

/**
 * The drawer's banner for an OPEN clinical reason. `role="status"`, never
 * `alert`: the R31 crisis banner in the review keeps the one assertive region on
 * these surfaces.
 */
export function DropOffClinicalCallout({ reason }: { reason: DropOffReasonKey }) {
  return (
    <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <strong>Clinician to call.</strong> Asked why they have not paid, the patient said &ldquo;
      {DROP_OFF_REASON_STAFF_LABEL[reason]}&rdquo;. That is a clinical question: a clinician should call them. Do not
      reply with an offer or a discount.
    </Alert>
  );
}

/* Decorative glyphs — `aria-hidden`, the chip's words carry the meaning. Drawn
   on the 24-unit grid the chip row's other glyphs use. */
function SpeechGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none' }}
    >
      <path d="M4 5h16v11H9l-5 4z" />
    </svg>
  );
}

function PhoneGlyph() {
  return (
    <svg aria-hidden="true" focusable="false" width={11} height={11} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }}>
      <path d="M6.6 2.5 9.4 6c.4.5.4 1.2 0 1.7L7.7 9.6a13 13 0 0 0 6.7 6.7l1.9-1.7c.5-.4 1.2-.4 1.7 0l3.5 2.8c.5.4.6 1.2.1 1.7l-1.6 1.9c-.8.9-2 1.2-3.1.8C9.6 19.6 4.4 14.4 2.2 7.1c-.4-1.1-.1-2.3.8-3.1l1.9-1.6c.5-.5 1.3-.4 1.7.1z" />
    </svg>
  );
}
